// O botão flutuante "Fale com a Bia": arrastável (movimento acima de 6 px é
// arrasto, não clique), com a posição e o "fechado" lembrados no navegador.
// Fechado, o cabeçalho ganha o botão de chamar a Bia de volta. Abre o
// copiloto; de lá, "Testar vendedor" abre o emulador. Os dois modais só
// baixam quando abertos, pra não pesar a casca. O botão vai por portal pro
// body: dentro do cabeçalho de vidro (backdrop-filter) o `fixed` fica preso.
import { Suspense, useEffect, useRef, useState } from 'react';
import { lazyDaTela } from '../../shared/recarga-de-chunk';
import { createPortal } from 'react-dom';
import { useAgentes } from '../agentes/api';
import estilos from './Copiloto.module.css';

const Copiloto = lazyDaTela(() => import('./Copiloto').then((m) => ({ default: m.Copiloto })));
const Emulador = lazyDaTela(() => import('../agentes/Emulador').then((m) => ({ default: m.Emulador })));

const CHAVE_ESCONDIDO = 'emu_fab_hidden', CHAVE_POSICAO = 'emu_fab_pos';
const lerLocal = (chave: string) => { try { return localStorage.getItem(chave); } catch { return null; } };
const gravarLocal = (chave: string, valor: string | null) => { try { if (valor === null) localStorage.removeItem(chave); else localStorage.setItem(chave, valor); } catch { /* modo anônimo */ } };

/** A posição presa à janela: o botão nunca some da tela num resize. */
export function prender(x: number, y: number, largura: number, altura: number, janela: { w: number; h: number }) {
  return { x: Math.min(Math.max(8, x), janela.w - largura - 8), y: Math.min(Math.max(8, y), janela.h - altura - 8) };
}

export function Bia() {
  const [escondido, setEscondido] = useState(() => lerLocal(CHAVE_ESCONDIDO) === '1');
  const [posicao, setPosicao] = useState<{ x: number; y: number } | null>(() => { try { const p = JSON.parse(lerLocal(CHAVE_POSICAO) || 'null'); return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null; } catch { return null; } });
  const [aberto, setAberto] = useState<'copiloto' | 'emulador' | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const botao = useRef<HTMLButtonElement>(null);
  const gesto = useRef<{ x0: number; y0: number; ox: number; oy: number; moveu: boolean } | null>(null);
  const agentes = useAgentes();

  useEffect(() => {
    const ao = () => setPosicao((p) => (p && botao.current ? prender(p.x, p.y, botao.current.offsetWidth, botao.current.offsetHeight, { w: innerWidth, h: innerHeight }) : p));
    window.addEventListener('resize', ao);
    return () => window.removeEventListener('resize', ao);
  }, []);

  const aoPressionar = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if ((e.target as HTMLElement).closest('[data-fechar]')) return;
    const r = e.currentTarget.getBoundingClientRect();
    gesto.current = { x0: e.clientX, y0: e.clientY, ox: r.left, oy: r.top, moveu: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* sem captura, sem drama */ }
  };
  const aoMover = (e: React.PointerEvent<HTMLButtonElement>) => {
    const g = gesto.current;
    if (!g) return;
    const dx = e.clientX - g.x0, dy = e.clientY - g.y0;
    if (!g.moveu && Math.hypot(dx, dy) < 6) return;
    g.moveu = true; setArrastando(true);
    setPosicao(prender(g.ox + dx, g.oy + dy, e.currentTarget.offsetWidth, e.currentTarget.offsetHeight, { w: innerWidth, h: innerHeight }));
  };
  const aoSoltar = () => {
    const g = gesto.current; gesto.current = null; setArrastando(false);
    if (g?.moveu && botao.current) { const r = botao.current.getBoundingClientRect(); gravarLocal(CHAVE_POSICAO, JSON.stringify({ x: Math.round(r.left), y: Math.round(r.top) })); setTimeout(() => { if (botao.current) botao.current.dataset.moveu = ''; }, 0); botao.current.dataset.moveu = '1'; }
  };
  const aoClicar = () => { if (botao.current?.dataset.moveu === '1') return; setAberto('copiloto'); };
  const esconder = () => { setEscondido(true); gravarLocal(CHAVE_ESCONDIDO, '1'); };
  const mostrar = () => { setEscondido(false); gravarLocal(CHAVE_ESCONDIDO, null); setAberto('copiloto'); };
  const padrao = agentes.data?.find((a) => a.is_default) ?? agentes.data?.[0];

  return (
    <>
      {escondido
        ? <button type="button" className={`botao discreto ${estilos.chamar}`} onClick={mostrar} title="Chamar a Bia de volta">💬 Bia</button>
        : createPortal(<button ref={botao} type="button" className={`${estilos.fab} ${arrastando ? estilos.arrastando : ''}`} style={posicao ? { left: posicao.x, top: posicao.y, right: 'auto', bottom: 'auto' } : undefined} title="Fale com a Bia" aria-label="Fale com a Bia" onPointerDown={aoPressionar} onPointerMove={aoMover} onPointerUp={aoSoltar} onPointerCancel={aoSoltar} onClick={aoClicar}>
            <span className={estilos.fabIcone} aria-hidden="true">b</span><span className={estilos.fabRotulo}>Fale com a Bia</span>
            <span className={estilos.fabFechar} data-fechar role="button" aria-label="Fechar o botão da Bia" title="Fechar" onClick={(e) => { e.stopPropagation(); esconder(); }}>✕</span>
          </button>, document.body)}
      {aberto && createPortal(<Suspense fallback={null}>
        {aberto === 'copiloto' && <Copiloto aoFechar={() => setAberto(null)} aoTestarVendedor={() => setAberto('emulador')} />}
        {aberto === 'emulador' && padrao && <Emulador agentes={agentes.data || []} inicial={padrao.id} rascunho={null} aoFechar={() => setAberto(null)} />}
      </Suspense>, document.body)}
    </>
  );
}
